<template>
	<div>
		<JuniperPageHeader back="/juniper/accessibility-stats" title="Engine stats" subtitle="Scan queue, running audits and ada-engine load" />

		<!-- Top strip -->
		<div class="axs-section axs-summary-section mb-3">
			<div class="axs-summary">
				<div class="axs-summary-item">
					<div class="axs-summary-label">Engine</div>
					<div class="axs-summary-value axs-summary-value--sm">
						<template v-if="engine">
							<span class="status-dot" :class="engine.online ? 'good' : 'poor'"></span>
							{{ engine.configured ? (engine.online ? 'Online' : 'Offline') : 'Not configured' }}
							<span v-if="engine.latency_ms !== null" class="cell-muted ms-2">{{ engine.latency_ms }} ms</span>
						</template>
						<span v-else class="cell-muted">—</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Queue · waiting / running</div>
					<div class="axs-summary-value">{{ queue.waiting }} / {{ queue.running }}</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Audits running</div>
					<div class="axs-summary-value">
						{{ running.length }}
						<span v-if="stuckCount" class="level-badge stuck ms-1">{{ stuckCount }} stuck</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Pages · last 24h</div>
					<div class="axs-summary-value">
						{{ day.pages_scanned || 0 }}
						<span class="axs-unit">scanned</span><span v-if="day.pages_failed" class="poor-text axs-unit">&nbsp;· {{ day.pages_failed }} failed</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Avg per page · 7d</div>
					<div class="axs-summary-value">{{ week.avg_page_s !== null && week.avg_page_s !== undefined ? week.avg_page_s + 's' : '—' }}</div>
				</div>
			</div>
		</div>

		<!-- Toolbar -->
		<div class="axs-section mb-3">
			<div class="filter-tabs-container">
				<div class="filter-tabs-group">
					<span class="ses-updated">
						<template v-if="data">Updated {{ updatedAgo }}</template>
						<template v-else-if="error">{{ error }}</template>
						<template v-else>Loading…</template>
					</span>
				</div>
				<div class="toolbar-right">
					<label class="ses-auto">
						<input type="checkbox" v-model="autoRefresh" />
						Auto-refresh {{ refreshSeconds }}s
					</label>
					<button class="toolbar-icon-btn" title="Reload" :disabled="loading" @click="load">
						<font-awesome-icon :icon="['fas', 'sync']" :spin="loading" />
					</button>
				</div>
			</div>
		</div>

		<!-- ada-engine instance -->
		<div class="axs-section mb-3">
			<div class="ses-head">
				<h3 class="ses-title">ada-engine instance</h3>
				<span v-if="stats" class="cell-muted">
					#{{ stats.instance.id }}<template v-if="stats.instance.revision"> · {{ stats.instance.revision }}</template> · up {{ duration(stats.instance.uptime_s) }}
				</span>
			</div>

			<div v-if="engine && !stats" class="ses-note">
				<template v-if="!engine.configured">ada-engine URL is not set (Juniper Settings → ADA Compliance).</template>
				<template v-else-if="!engine.online">Engine did not answer the health check{{ engine.error ? ': ' + engine.error : '' }}.</template>
				<template v-else-if="engine.error === 'stats HTTP 404'">Engine is online but has no <code>/stats</code> endpoint yet — deploy the latest ada-engine.</template>
				<template v-else>Could not read engine stats{{ engine.error ? ': ' + engine.error : '' }}.</template>
			</div>

			<template v-if="stats">
				<div class="axs-summary ses-engine">
					<div class="axs-summary-item">
						<div class="axs-summary-label">Scan slots</div>
						<div class="axs-summary-value">{{ stats.slots.active }} / {{ stats.slots.max }}</div>
						<div class="ses-meter">
							<span :style="{ width: pct(stats.slots.active, stats.slots.max) }" :class="{ full: stats.slots.active >= stats.slots.max }"></span>
						</div>
						<div class="ses-sub">
							{{ stats.slots.waiting }} waiting for a slot · mc2 sends {{ data.mc2_concurrency }} at once
						</div>
					</div>
					<div class="axs-summary-item">
						<div class="axs-summary-label">Scan time</div>
						<div class="axs-summary-value">{{ ms(stats.durations_ms.p50) }} <span class="axs-unit">p50</span></div>
						<div class="ses-sub">
							p95 {{ ms(stats.durations_ms.p95) }} · max {{ ms(stats.durations_ms.max) }} · last {{ stats.durations_ms.samples }}
						</div>
					</div>
					<div class="axs-summary-item">
						<div class="axs-summary-label">Scans since start</div>
						<div class="axs-summary-value">{{ stats.totals.scans }}</div>
						<div class="ses-sub">
							<span class="good-text">{{ stats.totals.ok }} ok</span> ·
							<span class="warn-text">{{ stats.totals.partial }} partial</span> ·
							<span class="poor-text">{{ stats.totals.failed }} failed</span>
						</div>
						<div class="ses-sub">
							engine errors: axe {{ stats.engine_failures.axe }} · ibm {{ stats.engine_failures.ibm }} · twac {{ stats.engine_failures.twac }}
						</div>
					</div>
					<div class="axs-summary-item">
						<div class="axs-summary-label">Memory</div>
						<div class="axs-summary-value">{{ stats.system.rss_mb }} <span class="axs-unit">MB</span></div>
						<div class="ses-meter">
							<span :style="{ width: pct(stats.system.rss_mb, stats.system.total_mem_mb) }"></span>
						</div>
						<div class="ses-sub">
							of {{ stats.system.total_mem_mb }} MB · {{ stats.system.cpus }} CPU · load {{ stats.system.load_avg[0] }}
						</div>
					</div>
					<div class="axs-summary-item">
						<div class="axs-summary-label">Chromium · PDF cache</div>
						<div class="axs-summary-value axs-summary-value--sm">
							<span class="status-dot" :class="stats.browser.connected ? 'good' : 'warn'"></span>
							{{ stats.browser.connected ? `${stats.browser.contexts} contexts · ${stats.browser.pages} pages` : stats.browser.launched ? 'disconnected' : 'not launched' }}
						</div>
						<div class="ses-sub">
							PDF hit rate {{ stats.pdf_cache.hit_rate !== null ? stats.pdf_cache.hit_rate + '%' : '—' }} · {{ stats.pdf_cache.scanned }} scanned
						</div>
					</div>
				</div>

				<div class="axs-table ses-table ses-inflight">
					<div class="table-header">
						<div class="header-cell">Scanning now</div>
						<div class="header-cell">Engines</div>
						<div class="header-cell text-end">Elapsed</div>
					</div>
					<div v-if="!stats.inflight.length" class="ses-empty">Nothing in flight on this instance.</div>
					<div v-for="s in stats.inflight" :key="s.url + s.started_at" class="table-row">
						<div class="table-cell" :title="s.url">{{ shortUrl(s.url) }}</div>
						<div class="table-cell">{{ s.engines.join(' · ') }}</div>
						<div class="table-cell text-end" :class="{ 'warn-text': s.elapsed_ms > 120000 }">{{ ms(s.elapsed_ms) }}</div>
					</div>
				</div>
			</template>
			<p class="ses-foot">
				Cloud Run can run several instances — this is the one that answered. Numbers reset when the instance restarts.
			</p>
		</div>

		<!-- Running audits -->
		<div class="axs-section mb-3">
			<div class="ses-head">
				<h3 class="ses-title">Running audits</h3>
			</div>
			<div class="axs-table ses-table ses-running">
				<div class="table-header">
					<div class="header-cell">Client</div>
					<div class="header-cell">Webpages</div>
					<div class="header-cell text-end">Files</div>
					<div class="header-cell">Started</div>
					<div class="header-cell text-end">Elapsed</div>
					<div class="header-cell text-end">ETA</div>
					<div class="header-cell">Run</div>
				</div>
				<div v-if="!running.length" class="ses-empty">No audit is running.</div>
				<div
					v-for="r in running"
					:key="r.id"
					class="table-row is-clickable"
					title="Reports & changes for this client"
					@click="$router.push({ name: 'juniperAccessibilityClientStats', params: { clientId: r.client_id } })"
				>
					<div class="table-cell client-cell">
						<span class="client-name-text">{{ r.client_name }}</span>
						<span class="client-url">{{ shortUrl(r.website) }} · report #{{ r.id }}</span>
					</div>
					<div class="table-cell">
						<div v-if="r.webpages" class="status-cell">
							<div class="status-bar">
								<span class="seg good" :style="{ width: pct(r.webpages.scanned, r.webpages.total) }"></span>
							</div>
							<span class="status-nums">
								{{ r.webpages.scanned }} / {{ r.webpages.total !== null ? r.webpages.total : '?' }}
								<span v-if="r.webpages.reused" class="cell-muted">({{ r.webpages.reused }} reused)</span>
							</span>
						</div>
						<span v-else class="cell-muted">starting…</span>
					</div>
					<div class="table-cell text-end">
						<template v-if="r.files">{{ r.files.scanned }} / {{ r.files.total !== null ? r.files.total : '?' }}</template>
						<span v-else class="cell-muted">—</span>
					</div>
					<div class="table-cell"><span class="time-text" :title="formatUtc(r.started_at)">{{ fromNowUtc(r.started_at) }}</span></div>
					<div class="table-cell text-end">{{ duration(r.elapsed_s) }}</div>
					<div class="table-cell text-end">{{ r.eta_s !== null ? '~' + duration(r.eta_s) : '—' }}</div>
					<div class="table-cell">
						<span v-if="r.stuck" class="level-badge stuck" title="Running longer than the 30 min job timeout — the worker probably died">stuck</span>
						<span v-else class="level-badge info">{{ r.auto ? 'auto' : 'manual' }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Queue -->
		<div class="axs-section mb-3">
			<div class="ses-head">
				<h3 class="ses-title">Queue <span class="cell-muted">ada-auditor</span></h3>
				<span v-if="queue.oldest_waiting_at" class="cell-muted">oldest waiting {{ fromNowUtc(queue.oldest_waiting_at) }}</span>
			</div>
			<div class="axs-table ses-table ses-queue">
				<div class="table-header">
					<div class="header-cell">Job</div>
					<div class="header-cell">Client</div>
					<div class="header-cell">State</div>
					<div class="header-cell text-end">Attempts</div>
					<div class="header-cell">Queued</div>
				</div>
				<div v-if="!queue.jobs.length" class="ses-empty">Queue is empty.</div>
				<div v-for="j in queue.jobs" :key="j.id" class="table-row">
					<div class="table-cell">{{ j.job }}<span v-if="j.report_id" class="cell-muted"> · #{{ j.report_id }}</span></div>
					<div class="table-cell">{{ j.client_name || '—' }}</div>
					<div class="table-cell">
						<span class="level-badge" :class="j.state === 'running' ? 'info' : 'waiting'">{{ j.state }}</span>
					</div>
					<div class="table-cell text-end">{{ j.attempts }}</div>
					<div class="table-cell"><span class="time-text" :title="formatUtc(j.created_at)">{{ fromNowUtc(j.created_at) }}</span></div>
				</div>
			</div>

			<template v-if="queue.failed_recent.length">
				<div class="ses-head ses-head--sub">
					<h3 class="ses-title">Failed jobs <span class="cell-muted">{{ queue.failed_7d }} in 7 days</span></h3>
				</div>
				<div class="axs-table ses-table ses-failed">
					<div v-for="f in queue.failed_recent" :key="f.id" class="table-row">
						<div class="table-cell">{{ f.job }}<span v-if="f.report_id" class="cell-muted"> · #{{ f.report_id }}</span></div>
						<div class="table-cell wrap poor-text" :title="f.error">{{ f.error }}</div>
						<div class="table-cell text-end"><span class="time-text" :title="formatUtc(f.failed_at)">{{ fromNowUtc(f.failed_at) }}</span></div>
					</div>
				</div>
			</template>
		</div>

		<!-- Activity -->
		<div class="axs-section mb-3">
			<div class="ses-head">
				<h3 class="ses-title">Pages per hour <span class="cell-muted">last 24h, incl. reused</span></h3>
				<span class="cell-muted">peak {{ hourlyMax }}</span>
			</div>
			<div class="ses-chart">
				<div v-for="h in hourly" :key="h.hour" class="ses-bar-col" :title="`${formatUtc(h.hour, 'ddd h A')}: ${h.pages} pages`">
					<span class="ses-bar" :style="{ height: hourlyMax ? (100 * h.pages) / hourlyMax + '%' : '0' }"></span>
				</div>
			</div>
			<div class="ses-chart-axis">
				<span>{{ hourly.length ? formatUtc(hourly[0].hour, 'h A') : '' }}</span>
				<span>now</span>
			</div>

			<div class="axs-table ses-table ses-activity">
				<div class="table-header">
					<div class="header-cell"></div>
					<div class="header-cell text-end">Reports</div>
					<div class="header-cell text-end">Scanned</div>
					<div class="header-cell text-end">Reused</div>
					<div class="header-cell text-end">Failed</div>
					<div class="header-cell text-end">Avg report</div>
					<div class="header-cell text-end">Avg / page</div>
				</div>
				<div v-for="w in windows" :key="w.label" class="table-row">
					<div class="table-cell">{{ w.label }}</div>
					<div class="table-cell text-end">{{ w.v.reports_finished }}<span v-if="w.v.reports_started !== w.v.reports_finished" class="cell-muted"> / {{ w.v.reports_started }}</span></div>
					<div class="table-cell text-end">{{ w.v.pages_scanned }}</div>
					<div class="table-cell text-end">{{ w.v.pages_reused }}</div>
					<div class="table-cell text-end" :class="{ 'poor-text': w.v.pages_failed }">{{ w.v.pages_failed }}</div>
					<div class="table-cell text-end">{{ w.v.avg_report_s !== null ? duration(w.v.avg_report_s) : '—' }}</div>
					<div class="table-cell text-end">{{ w.v.avg_page_s !== null ? w.v.avg_page_s + 's' : '—' }}</div>
				</div>
			</div>
			<p class="ses-foot">
				Reports: finished / started. Failed = pages that failed twice and were left out of the report. Avg / page is wall-clock
				per freshly scanned page (pages run in parallel).
			</p>
		</div>

		<!-- Upcoming + PDF jobs -->
		<div class="axs-section mb-4">
			<div class="ses-head">
				<h3 class="ses-title">Scheduled <span class="cell-muted">next 24h</span></h3>
				<span class="cell-muted">{{ upcoming.length }} audits</span>
			</div>
			<div class="axs-table ses-table ses-upcoming">
				<div v-if="!upcoming.length" class="ses-empty">Nothing scheduled in the next 24 hours.</div>
				<div v-for="u in upcomingShown" :key="u.client_id" class="table-row">
					<div class="table-cell">{{ u.client_name }}</div>
					<div class="table-cell"><span class="interval-badge">{{ u.interval }}</span></div>
					<div class="table-cell text-end">
						<span class="next-scan" :class="{ due: isPast(u.next_run_at) }" :title="formatUtc(u.next_run_at)">
							{{ isPast(u.next_run_at) ? 'due ' : '' }}{{ fromNowUtc(u.next_run_at) }}
						</span>
					</div>
				</div>
				<button v-if="upcoming.length > upcomingShown.length" type="button" class="ses-more" @click="upcomingLimit += 20">
					Show more ({{ upcoming.length - upcomingShown.length }})
				</button>
			</div>

			<template v-if="pdfJobs.length">
				<div class="ses-head ses-head--sub">
					<h3 class="ses-title">PDF scan jobs</h3>
				</div>
				<div class="axs-table ses-table ses-pdf">
					<div v-for="p in pdfJobs" :key="p.id" class="table-row">
						<div class="table-cell">{{ p.name }}</div>
						<div class="table-cell"><span class="level-badge" :class="p.status === 'running' ? 'info' : 'waiting'">{{ p.status }}</span></div>
						<div class="table-cell">
							<div class="status-cell">
								<div class="status-bar">
									<span class="seg good" :style="{ width: pct(p.processed_files, p.total_files) }"></span>
								</div>
								<span class="status-nums">{{ p.processed_files }} / {{ p.total_files }}</span>
							</div>
						</div>
						<div class="table-cell" :title="p.current_file_name">{{ p.current_client || '' }} {{ p.current_file_name ? '· ' + p.current_file_name : '' }}</div>
						<div class="table-cell text-end" :class="{ 'poor-text': p.failed_count }">{{ p.failed_count }} failed</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import moment from 'moment-timezone'
import api from '@/api'
import { fromNowUtc, formatUtc } from '@/utils/time'

const REFRESH_SECONDS = 15

export default {
	name: 'AccessibilityEngineStats',
	data() {
		return {
			data: null,
			loading: false,
			error: null,
			autoRefresh: true,
			refreshSeconds: REFRESH_SECONDS,
			timer: null,
			now: Date.now(),
			upcomingLimit: 10,
		}
	},
	computed: {
		engine() {
			return this.data ? this.data.engine : null
		},
		stats() {
			return this.engine ? this.engine.stats : null
		},
		queue() {
			return this.data ? this.data.queue : { waiting: 0, running: 0, jobs: [], failed_recent: [], failed_7d: 0 }
		},
		running() {
			return this.data ? this.data.running : []
		},
		stuckCount() {
			return this.running.filter((r) => r.stuck).length
		},
		day() {
			return this.data ? this.data.activity.day : {}
		},
		week() {
			return this.data ? this.data.activity.week : {}
		},
		windows() {
			if (!this.data) return []
			return [
				{ label: 'Last 24h', v: this.day },
				{ label: 'Last 7 days', v: this.week },
			]
		},
		hourly() {
			return this.data ? this.data.activity.hourly : []
		},
		hourlyMax() {
			return this.hourly.reduce((m, h) => Math.max(m, h.pages), 0)
		},
		upcoming() {
			return this.data ? this.data.upcoming : []
		},
		upcomingShown() {
			return this.upcoming.slice(0, this.upcomingLimit)
		},
		pdfJobs() {
			return this.data ? this.data.pdf_jobs : []
		},
		updatedAgo() {
			const s = Math.max(0, Math.round((this.now - moment.utc(this.data.generated_at).valueOf()) / 1000))
			return s < 5 ? 'just now' : `${s}s ago`
		},
	},
	watch: {
		autoRefresh() {
			this.schedule()
		},
	},
	created() {
		this.load()
		this.clock = setInterval(() => {
			this.now = Date.now()
		}, 1000)
		document.addEventListener('visibilitychange', this.onVisibility)
	},
	beforeDestroy() {
		clearTimeout(this.timer)
		clearInterval(this.clock)
		document.removeEventListener('visibilitychange', this.onVisibility)
	},
	methods: {
		fromNowUtc,
		formatUtc,
		async load() {
			clearTimeout(this.timer)
			this.loading = true
			try {
				const { data } = await api.get('/juniper/ada-auditor/stats/engine')
				this.data = data.data
				this.error = null
			} catch (e) {
				this.error = (e.response && e.response.data && e.response.data.message) || 'Could not load engine stats'
			} finally {
				this.loading = false
				this.schedule()
			}
		},
		// Next poll only while the tab is visible and auto-refresh is on.
		schedule() {
			clearTimeout(this.timer)
			if (this.autoRefresh && !document.hidden) {
				this.timer = setTimeout(this.load, REFRESH_SECONDS * 1000)
			}
		},
		onVisibility() {
			if (document.hidden) clearTimeout(this.timer)
			else if (this.autoRefresh) this.load()
		},
		pct(part, total) {
			return total ? Math.min(100, (100 * part) / total) + '%' : '0%'
		},
		ms(v) {
			if (v === null || v === undefined) return '—'
			return v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(1)}s`
		},
		duration(s) {
			if (s === null || s === undefined) return '—'
			if (s < 60) return `${s}s`
			if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
			const h = Math.floor(s / 3600)
			return h < 48 ? `${h}h ${Math.floor((s % 3600) / 60)}m` : `${Math.floor(h / 24)}d ${h % 24}h`
		},
		shortUrl(url) {
			return (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '')
		},
		isPast(ts) {
			return moment.utc(ts).valueOf() < this.now
		},
	},
}
</script>

<style scoped src="./accessibility-stats.css"></style>

<style scoped>
.axs-unit {
	font-size: 12px;
	font-weight: 500;
	color: #6b7280;
}

.ses-updated,
.ses-auto {
	font-family: var(--axs-mono);
	font-size: 12px;
	color: #6b7280;
}

.ses-auto {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin: 0;
	cursor: pointer;
}

.ses-head {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 24px 10px;
	font-family: var(--axs-mono);
	font-size: 12px;
}

.ses-head--sub {
	border-top: 1px solid #f3f4f6;
}

.ses-title {
	font-family: var(--axs-mono);
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	margin: 0;
}

.ses-note,
.ses-empty,
.ses-foot {
	font-size: 13px;
	color: #6b7280;
	padding: 10px 24px;
	margin: 0;
}

.ses-foot {
	font-size: 12px;
	border-top: 1px solid #f3f4f6;
}

.ses-engine {
	border-top: 1px solid #f3f4f6;
}

.ses-sub {
	font-family: var(--axs-mono);
	font-size: 11px;
	color: #6b7280;
	margin-top: 4px;
}

.ses-meter {
	height: 6px;
	background: #f3f4f6;
	border-radius: 3px;
	overflow: hidden;
	margin-top: 6px;
}

.ses-meter span {
	display: block;
	height: 100%;
	background: #93c5fd;
}

.ses-meter span.full {
	background: #fbbf24;
}

/* Section tables — own column sets on top of the shared table look */
.ses-table .table-header,
.ses-table .table-row {
	min-width: 0;
}

.ses-inflight .table-header,
.ses-inflight .table-row {
	grid-template-columns: minmax(200px, 1fr) 140px 90px;
}

.ses-running .table-header,
.ses-running .table-row {
	grid-template-columns: minmax(200px, 1.4fr) minmax(180px, 1fr) 80px 110px 80px 80px 70px;
	min-width: 900px;
}

.ses-queue .table-header,
.ses-queue .table-row {
	grid-template-columns: minmax(200px, 1fr) minmax(160px, 1fr) 90px 70px 120px;
	min-width: 720px;
}

.ses-failed .table-row {
	grid-template-columns: 220px 1fr 120px;
	min-width: 640px;
}

.ses-activity .table-header,
.ses-activity .table-row {
	grid-template-columns: 120px repeat(6, minmax(70px, 1fr));
	min-width: 680px;
}

.ses-upcoming .table-row {
	grid-template-columns: minmax(200px, 1fr) 90px 140px;
}

.ses-pdf .table-row {
	grid-template-columns: minmax(160px, 1fr) 90px minmax(160px, 1fr) minmax(160px, 1fr) 80px;
	min-width: 780px;
}

.level-badge.stuck {
	background: #fee2e2;
	color: #991b1b;
}

.level-badge.waiting {
	background: #f3f4f6;
	color: #374151;
}

.ses-more {
	background: none;
	border: 0;
	padding: 8px 24px 12px;
	font-size: 12px;
	color: #6b7280;
}

.ses-more:hover {
	color: #111827;
}

/* Hourly bars */
.ses-chart {
	display: flex;
	align-items: flex-end;
	gap: 3px;
	height: 90px;
	padding: 0 24px;
}

.ses-bar-col {
	flex: 1;
	height: 100%;
	display: flex;
	align-items: flex-end;
}

.ses-bar {
	display: block;
	width: 100%;
	min-height: 2px;
	background: #93c5fd;
	border-radius: 2px 2px 0 0;
}

.ses-bar-col:hover .ses-bar {
	background: #1565c0;
}

.ses-chart-axis {
	display: flex;
	justify-content: space-between;
	padding: 4px 24px 12px;
	font-family: var(--axs-mono);
	font-size: 11px;
	color: #9ca3af;
}
</style>
